import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, thumbUrl } from "@/lib/api";
import { Hash, Search } from "lucide-react";

function formatPrice(p, currency = "gbp") {
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: currency.toUpperCase() }).format(p);
  } catch {
    return `£${Number(p).toFixed(2)}`;
  }
}

const fmtDate = (iso) => {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

export default function FindMyNumber() {
  const [params, setParams] = useSearchParams();
  const [num, setNum] = useState(params.get("n") || "");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const run = useCallback(async (value) => {
    const clean = String(value || "").replace(/\D/g, "").slice(0, 4);
    if (!clean) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.get(`/search/race-number/${clean}`);
      setResult(r.data);
    } catch {
      setError("Enter a race number between 1 and 4 digits.");
      setResult(null);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    const initial = params.get("n");
    if (initial) run(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once for deep link
  }, []);

  const submit = (e) => {
    e.preventDefault();
    setParams(num ? { n: num } : {});
    run(num);
  };

  const grouped = result
    ? Object.entries(
        result.images.reduce((acc, img) => {
          const key = img.event_id || "other";
          (acc[key] = acc[key] || []).push(img);
          return acc;
        }, {})
      )
    : [];

  return (
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 mt-8 pb-24" data-testid="find-my-number-page">
      <div className="mt-8 max-w-2xl">
        <p className="text-xs uppercase tracking-[0.3em] text-white/40">Rider search</p>
        <h1 className="font-serif text-5xl md:text-6xl font-light leading-none mt-3">
          Find my <span className="pm-orange">number</span>
        </h1>
        <p className="text-white/65 mt-6 leading-relaxed">
          Type your race number and we'll pull every photo we've spotted it in, across all events.
          Numbers are read automatically — if a shot of you is missing, it may just not be tagged yet.
        </p>
        <form onSubmit={submit} className="mt-8 flex" data-testid="number-search-form">
          <div className="relative flex-1">
            <Hash size={18} className="absolute left-4 top-1/2 -translate-y-1/2 pm-orange pointer-events-none" />
            <input
              type="text"
              inputMode="numeric"
              value={num}
              onChange={(e) => setNum(e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="Your race number, e.g. 27"
              className="w-full bg-black/60 border border-white/25 pl-11 pr-4 py-4 text-lg text-white placeholder:text-white/35 focus:outline-none focus:border-[var(--pm-orange)] transition-colors font-display tracking-widest"
              data-testid="number-search-input"
            />
          </div>
          <button
            type="submit"
            disabled={busy || !num}
            className="bg-[var(--pm-orange)] text-black px-8 text-xs uppercase tracking-[0.25em] font-bold hover:opacity-90 transition disabled:opacity-50"
            data-testid="number-search-submit"
          >
            <Search size={16} />
          </button>
        </form>
        {error && <p className="text-red-400 text-sm mt-4" data-testid="number-search-error">{error}</p>}
      </div>

      {busy && <p className="text-white/50 text-sm mt-12 uppercase tracking-[0.3em]">Searching…</p>}

      {result && !busy && (
        <div className="mt-12" data-testid="number-search-results">
          <p className="text-xs uppercase tracking-[0.2em] text-white/50 border-b border-white/10 pb-4">
            <span className="pm-orange font-semibold">{result.images.length}</span> photo{result.images.length !== 1 ? "s" : ""} found for <span className="pm-orange font-semibold">#{result.number}</span>
          </p>
          {result.images.length === 0 && (
            <p className="text-white/50 text-sm mt-8" data-testid="number-search-empty">
              No tagged photos for #{result.number} yet. Try again after the next event, or browse the
              {" "}<Link to="/events" className="pm-orange underline underline-offset-4">event galleries</Link> — some numbers are hard to read automatically.
            </p>
          )}
          {grouped.map(([evId, imgs]) => {
            const ev = result.events[evId];
            return (
              <div key={evId} className="mt-10">
                <div className="flex items-baseline gap-4 mb-4">
                  <h3 className="font-serif text-2xl">{ev ? ev.track : "Other"}</h3>
                  {ev && <span className="text-xs uppercase tracking-[0.2em] text-white/40">{fmtDate(ev.date)}{ev.name ? ` · ${ev.name}` : ""}</span>}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {imgs.map((img) => (
                    <Link key={img.id} to={`/image/${img.id}`} className="gallery-card block aspect-[4/5]" data-testid={`number-result-${img.id}`}>
                      <img src={thumbUrl(img.id)} alt={img.title} loading="lazy" className="w-full h-full object-cover" />
                      <div className="reveal">
                        <div>
                          <h4 className="font-serif text-xl text-white leading-tight">{img.title}</h4>
                          <p className="text-xs uppercase tracking-[0.2em] text-white/60 mt-1">{formatPrice(img.price, img.currency)}</p>
                        </div>
                        <span className="text-xs uppercase tracking-[0.2em] text-white border border-white/40 px-3 py-2">View</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
